import { useContext } from 'react';
import pluralize from 'pluralize';

import { media } from 'context';
import { Clickable, Icon, Layout, PillButton, ProductImg, Text } from 'ds';
import AddQuantity from 'ds/AddQuantity';
import { ConsumablesProduct, ConsumablesProductSubscription } from 'shared';
import { selectConsumablesOrders, selectProductSubscriptions } from 'store/Consumables/selectors';
import { useAppSelector } from 'store/hooks';

import QuantityPillButton from './QuantityPillButton';
import PriceTag from '../PriceTag';

interface ConsumablesProductCardProps {
  product: ConsumablesProduct;
  editSubscription?: boolean;
  subscriptionQuantity?: number;
  showQuantityPillMobile?: boolean;
  mobileShop?: boolean;
  showAddButton?: boolean;
  desktopWidth?: number;
  mobilePaddingLeft?: number;
  unitLabel?: string;
  onButtonClick: () => void;
  onProductCardClick: () => void;
  useAddQuantity?: boolean;
  currentQuantity?: number;
  onAddQuantityClick?: (quantity: number) => void;
}

const ConsumablesProductCard = ({
  product,
  editSubscription,
  subscriptionQuantity,
  showQuantityPillMobile = false,
  mobileShop = false,
  showAddButton = false,
  desktopWidth = 184,
  mobilePaddingLeft,
  unitLabel,
  onButtonClick,
  onProductCardClick,
  useAddQuantity = false,
  currentQuantity,
  onAddQuantityClick
}: ConsumablesProductCardProps) => {
  const { image_key, amount_cents, unit_type, units_per_package, package_type, brand, detail } = product;
  const { isMobile } = useContext(media);
  const consumableSubscriptions = useAppSelector(selectProductSubscriptions);
  const consumablesOrders = useAppSelector(selectConsumablesOrders);
  const itemsInSubscription = consumableSubscriptions?.find(
    (csp: ConsumablesProductSubscription) => csp.consumables_product.id === product.id
  )?.order_quantity;

  return (
    <Clickable
      onClick={e => {
        e.stopPropagation();
        onProductCardClick();
      }}
    >
      <Layout
        align="flex-start"
        {...(isMobile
          ? {
              direction: 'column',
              minHeight: isMobile ? (useAddQuantity ? 354 : 298) : undefined,
              paddingLeft: mobilePaddingLeft
            }
          : { flex: true, direction: 'column', width: 184, height: '100%' })}
      >
        <Layout position="relative">
          <ProductImg
            srcKey={image_key}
            {...(isMobile
              ? {
                  style: {
                    width: mobileShop ? 134 : 158,
                    height: mobileShop ? 134 : 158,
                    minWidth: mobileShop ? 134 : 158
                  }
                }
              : { style: { width: 184, height: 184 } })}
            size="sm"
            alt={detail}
          />
          {isMobile && showQuantityPillMobile && (
            <Layout position="absolute" bottom={4} right={4}>
              <QuantityPillButton quantity={subscriptionQuantity ?? 0} />
            </Layout>
          )}
          {showAddButton && (
            <Layout
              position="absolute"
              bottom={4}
              right={4}
              borderRadius="circular"
              color="blue-500"
              width={32}
              height={32}
              align="center"
              justify="center"
            >
              <Icon name="plus" size="md" color="white" />
            </Layout>
          )}
        </Layout>
        <Layout marginTop={16} {...(isMobile ? { justify: 'flex-start', width: '100%' } : {})}>
          <PriceTag amountCents={amount_cents} unitLabel={unitLabel} />
        </Layout>
        <Layout marginTop={4} height={80} {...(isMobile ? { marginBottom: 12 } : { marginBottom: 8 })}>
          <Text size="body-sm" maxLines={4} align="left">
            {brand} {detail} {pluralize(unit_type, 2)} ({units_per_package}/{package_type})
          </Text>
        </Layout>
        {!isMobile &&
          (editSubscription ? (
            <PillButton
              fullWidth
              icon="editPen"
              type="secondaryOutline"
              size="md"
              text={
                <Layout marginRight={4}>
                  <Text>{subscriptionQuantity}</Text>
                </Layout>
              }
              borderRadius={36}
              onClick={onButtonClick}
              iconSize="xs"
              iconPosition="right"
            />
          ) : (
            <Layout maxWidth={isMobile ? undefined : desktopWidth} width={isMobile ? '100%' : desktopWidth}>
              {useAddQuantity && currentQuantity !== undefined && onAddQuantityClick ? (
                <AddQuantity
                  value={currentQuantity}
                  size="sm"
                  handleDecreaseQuantity={() => {
                    onAddQuantityClick(currentQuantity - 1);
                  }}
                  handleIncreaseQuantity={() => {
                    onAddQuantityClick(currentQuantity + 1);
                  }}
                />
              ) : (
                <PillButton
                  type="primary"
                  size="md"
                  text="Add"
                  borderRadius={36}
                  onClick={e => {
                    e.stopPropagation();
                    onButtonClick();
                  }}
                  fullWidth
                />
              )}
            </Layout>
          ))}
        {isMobile && useAddQuantity && currentQuantity !== undefined && onAddQuantityClick && (
          <Layout width="100%">
            <AddQuantity
              value={currentQuantity}
              size="sm"
              handleDecreaseQuantity={() => {
                onAddQuantityClick(currentQuantity - 1);
              }}
              handleIncreaseQuantity={() => {
                onAddQuantityClick(currentQuantity + 1);
              }}
            />
          </Layout>
        )}
        {!editSubscription && itemsInSubscription && (
          <Layout marginTop={isMobile ? 4 : 8} flex justify={isMobile ? 'flex-start' : 'center'} width="100%">
            <Text size="body-sm" color="gray-400">
              {itemsInSubscription} in {!consumablesOrders || consumablesOrders.length === 0 ? 'cart' : 'subscription'}
            </Text>
          </Layout>
        )}
      </Layout>
    </Clickable>
  );
};

export default ConsumablesProductCard;
